import { checkContrast, CONTRAST_THRESHOLD, deepMerge, shade, tint } from '../../utils';

import {
    PalettePrimaryBase,
    PalettePrimaryContrastText,
    PaletteSecondaryBase,
    PaletteSecondaryContrastText,
    PaletteTertiaryBase,
    PaletteTertiaryContrastText,
    PaletteAccentBase,
    PaletteAccentContrastText,
    PaletteTextHeading,
    PaletteTextPrice,
    PaletteTextLink,
    PaletteActionFocus,
} from '../../design-tokens/honeycomb-tokens';
import {
    ExtendedColorOverride,
    ExtendedColor,
    HoneycombPalette,
    HoneycombPaletteOverrides,
    ColorMappings,
    ExtendableColor,
    MappableColor,
    MappableColors,
    SystemColorsOverride,
    SystemColor,
    ColorTints,
} from '../types/HoneycombPalette';

const defaultPrimary: ExtendedColorOverride = {
    base: PalettePrimaryBase,
    contrastText: PalettePrimaryContrastText,
};

const defaultSecondary: ExtendedColorOverride = {
    base: PaletteSecondaryBase,
    contrastText: PaletteSecondaryContrastText,
};

const defaultTertiary: ExtendedColorOverride = {
    base: PaletteTertiaryBase,
    contrastText: PaletteTertiaryContrastText,
};

const defaultAccent: ExtendedColorOverride = {
    base: PaletteAccentBase,
    contrastText: PaletteAccentContrastText,
};

const defaultGrey: ExtendedColorOverride = {
    contrastText: '#000000',
    tint: {
        '100': '#F1F1F1',
        '200': '#CECECE',
        '300': '#ACACAC',
        '400': '#898989',
        '500': '#676767',
        '600': '#444444',
        '700': '#222222',
    },
};

const defaultSystem: SystemColorsOverride = {
    success: {
        contrastText: '#000000',
        tint: {
            '100': '#E6F2E6',
            '200': '#99CBA3',
            '300': '#4DA461',
            '400': '#007D1E',
            '500': '#00690A',
            '600': '#005500',
            '700': '#004100 ',
        },
    },
    error: {
        contrastText: '#000000',
        tint: {
            '100': '#FFF2F2',
            '200': '#E2AFB1',
            '300': '#C46D71',
            '400': '#A82A30',
            '500': '#94161C',
            '600': '#800208',
            '700': '#6C0000',
        },
    },
    info: {
        contrastText: '#000000',
        tint: {
            '100': '#F2F9FF',
            '200': '#ABCBE7',
            '300': '#649DCF',
            '400': '#1D70B8',
            '500': '#095CA4',
            '600': '#094890',
            '700': '#09347C',
        },
    },
    warning: {
        contrastText: '#000000',
        tint: {
            '100': '#FCF0E7',
            '200': '#F4BB9A',
            '300': '#EC864D',
            '400': '#E55200',
            '500': '#D13E00',
            '600': '#BD2A00',
            '700': '#A91600',
        },
    },
};

const defaultMappings: ColorMappings = {
    neutral: 'grey',
};

const defaultCommon = {
    white: '#FFFFFF',
    black: '#000000',
};

const defaultText = {
    body: defaultGrey.tint?.[700] as string,
    light: defaultGrey.tint?.[500] as string,
    inverse: '#FFFFFF',
    heading: PaletteTextHeading,
    price: PaletteTextPrice,
    link: PaletteTextLink,
    field: defaultGrey.tint?.[700] as string,
};

const defaultBackground = {
    body: defaultGrey.tint?.[100] as string,
    content: '#FFFFFF',
    offset: defaultGrey.tint?.[100] as string,
};

const defaultDivider = defaultGrey.tint?.[100] as string;
const defaultBorder = defaultGrey.tint?.[200] as string;
const defaultStarRating = '#FFB700';

const social = {
    facebook: {
        blue: '#4267B2',
    },
};

const tintLevels = ['100', '200', '300', '400', '500', '600', '700'];
const tintStep = 0.15; // tweak this for light/dark intensity per step

export function createPalette(overrides?: HoneycombPaletteOverrides): HoneycombPalette {
    const {
        primary = defaultPrimary,
        secondary = defaultSecondary,
        tertiary = defaultTertiary,
        accent = defaultAccent,
        accentPrimary = defaultAccent,
        accentSecondary = defaultAccent,
        accentTertiary = defaultAccent,
        grey = defaultGrey,
        neutral, // Mappable
        mappings = defaultMappings,
        system = defaultSystem,
        ...other
    } = overrides || {};

    const bodyText = overrides?.text?.body ?? defaultText.body;
    const inverseText = overrides?.text?.inverse ?? defaultText.inverse;

    function extendColor(variant: ExtendedColorOverride): ExtendedColor {
        const baseTint = variant.baseTint ?? '400';

        if (variant.tint && Object.keys(variant.tint).length === tintLevels.length) {
            const tints = variant.tint as ColorTints;
            const base = variant.base ?? tints[baseTint];

            return {
                base,
                tint: tints,
                lightest: tints['100'],
                lighter: tints['200'],
                light: tints['300'],
                dark: tints['700'],
                contrastText: variant.contrastText || checkContrast(base, inverseText) ? inverseText : bodyText,
                baseTint,
            };
        }

        if (!variant.base) {
            throw new Error('Either base color or full tint object must be provided');
        }

        const { base } = variant;
        const baseNum = parseInt(baseTint);

        const computedTints = Object.fromEntries(
            tintLevels.map((level) => {
                const levelNum = parseInt(level);
                const stepsAway = Math.abs(levelNum - baseNum) / 100;
                const offsetAmount = stepsAway * tintStep;

                let adjustedColor: string;
                if (levelNum < baseNum) {
                    // lighter tints (tint = mix with white)
                    adjustedColor = tint(base, offsetAmount);
                } else if (levelNum > baseNum) {
                    // darker tints (shade = mix with black)
                    adjustedColor = shade(base, offsetAmount);
                } else {
                    // base tint stays the base color itself
                    adjustedColor = base;
                }

                return [level, adjustedColor];
            })
        ) as ColorTints;

        return {
            base,
            tint: computedTints,
            lightest: computedTints['100'],
            lighter: computedTints['200'],
            light: computedTints['300'],
            dark: computedTints['700'],
            contrastText: variant.contrastText || checkContrast(base, inverseText) ? inverseText : bodyText,
            baseTint,
        };
    }

    const extendedColors: { [key in ExtendableColor | SystemColor]: ExtendedColor } = {
        primary: extendColor(primary),
        secondary: extendColor(secondary),
        tertiary: extendColor(tertiary),
        accent: extendColor(accent),
        accentPrimary: extendColor(accentPrimary),
        accentSecondary: extendColor(accentSecondary),
        accentTertiary: extendColor(accentTertiary),
        grey: extendColor(grey),
        success: extendColor(system.success),
        error: extendColor(system.error),
        warning: extendColor(system.warning),
        info: extendColor(system.info),
    };

    const mappableColors = Object.fromEntries(
        Object.keys(mappings).map((key) => {
            // Unless they have been explicitly defined, mapped colours will reuse one of our extendable colour.
            const extendedColor = neutral ? extendColor(neutral) : extendedColors[mappings[key as MappableColor]];
            return [key as MappableColor, extendedColor];
        })
    ) as MappableColors;

    return deepMerge(
        {
            primary: extendedColors.primary,
            secondary: extendedColors.secondary,
            tertiary: extendedColors.tertiary,
            accent: extendedColors.accent,
            accentPrimary: extendedColors.accentPrimary,
            accentSecondary: extendedColors.accentSecondary,
            accentTertiary: extendedColors.accentTertiary,
            grey: extendedColors.grey,
            neutral: mappableColors.neutral,
            common: defaultCommon,
            text: defaultText,
            background: defaultBackground,
            divider: defaultDivider,
            border: defaultBorder,
            starRating: defaultStarRating,
            system: {
                success: extendedColors.success,
                error: extendedColors.error,
                warning: extendedColors.warning,
                info: extendedColors.info,
            },
            action: {
                focus: PaletteActionFocus,
            },
            contrastThreshold: CONTRAST_THRESHOLD,
            social,
        },
        other
    );
}
